import { useDataQuality } from '@/hooks/useLocalStorage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, Copy, Unlink, Recycle, CalendarX, RefreshCw, Wrench, Eye, Trash2, Check } from 'lucide-react';
import { dataSource } from '@/lib/dataSource';
import { useState } from 'react';

const severityColors = {
  HIGH: 'bg-red-100 text-red-800 border-red-200',
  MEDIUM: 'bg-orange-100 text-orange-800 border-orange-200',
  LOW: 'bg-yellow-100 text-yellow-800 border-yellow-200'
};

const typeIcons = {
  DUPLICATE: Copy,
  ORPHAN: Unlink,
  CYCLE: Recycle,
  OVERLAP: CalendarX
};

const typeColors = {
  DUPLICATE: 'text-red-600',
  ORPHAN: 'text-orange-600',
  CYCLE: 'text-purple-600',
  OVERLAP: 'text-yellow-600'
};

export function DataQuality() {
  const { data: issues, isLoading, isFetching, refetch } = useDataQuality();
  const { toast } = useToast();
  const [fixingIssue, setFixingIssue] = useState<string | null>(null);
  const [viewingIssue, setViewingIssue] = useState<any | null>(null);

  const stats = {
    duplicates: issues?.filter(i => i.type === 'DUPLICATE').length || 0,
    orphans: issues?.filter(i => i.type === 'ORPHAN').length || 0,
    cycles: issues?.filter(i => i.type === 'CYCLE').length || 0,
    overlaps: issues?.filter(i => i.type === 'OVERLAP').length || 0
  };

  const handleFixIssue = async (issue: any) => {
    setFixingIssue(issue.id);
    
    try {
      if (issue.type === 'DUPLICATE') {
        // For duplicates, we'll delete the problematic duplicate code
        const entityKey = issue.entityKey.split(':');
        if (entityKey.length >= 2) {
          const type = entityKey[0];
          const codeName = entityKey[1];
          
          // Find codes with duplicate names and delete the test ones
          const codes = await dataSource.listCodes(type);
          const duplicateCodes = codes.filter(c => c.name.toLowerCase() === codeName.toLowerCase());
          
          // Delete codes created by test-user (the problematic ones)
          for (const code of duplicateCodes) {
            if (code.createdBy === 'test-user') {
              await dataSource.deleteCode(code.type, code.code);
            }
          }
        }
      } else if (issue.type === 'ORPHAN') {
        // For orphans, delete the orphaned code
        const entityKey = issue.entityKey.split(':');
        if (entityKey.length >= 2) {
          const type = entityKey[0];
          const code = entityKey[1];
          await dataSource.deleteCode(type, code);
        }
      }
      
      toast({
        title: "Issue Fixed",
        description: `Successfully resolved ${issue.type.toLowerCase()} issue: ${issue.title}`,
      });
      
      // Refresh the data quality analysis
      refetch();
    } catch (error) {
      toast({
        title: "Fix Failed",
        description: `Failed to fix issue: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setFixingIssue(null);
    }
  };

  const handleViewIssue = (issue: any) => {
    setViewingIssue(issue);
  };

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="mb-4 md:mb-6">
        <h2 className="text-xl md:text-xl font-semibold text-gray-900">Data Quality Dashboard</h2>
        <p className="text-gray-600 mt-1 text-md md:text-base">Identify and resolve data integrity issues</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs md:text-sm font-medium text-gray-600 truncate">Duplicates</p>
                <p className="text-lg md:text-2xl font-bold text-red-600">{stats.duplicates}</p>
              </div>
              <div className="w-8 h-8 md:w-10 md:h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Copy className="h-4 w-4 md:h-5 md:w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Orphans</p>
                <p className="text-2xl font-bold text-orange-600">{stats.orphans}</p>
              </div>
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Unlink className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Cycles</p>
                <p className="text-2xl font-bold text-purple-600">{stats.cycles}</p>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Recycle className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Overlaps</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.overlaps}</p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <CalendarX className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Issues List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Data Quality Issues</CardTitle>
            <Button onClick={() => refetch()} variant="outline" disabled={isFetching}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
              {isFetching ? 'Scanning…' : 'Run DQ Scan'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {issues && issues.length > 0 ? (
              issues.map((issue) => {
                const TypeIcon = typeIcons[issue.type];
                const iconColor = typeColors[issue.type];
                
                return (
                  <div
                    key={issue.id}
                    className={`flex items-center justify-between p-4 border rounded-lg ${severityColors[issue.severity]}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                        <TypeIcon className={`h-4 w-4 ${iconColor}`} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{issue.title}</div>
                        <div className="text-sm text-gray-700">{issue.description}</div>
                        <div className="text-xs text-gray-600 mt-1">{issue.path}</div>
                        <div className="flex items-center space-x-2 mt-2">
                          <Badge variant="outline">{issue.type}</Badge>
                          <Badge variant="outline">{issue.severity}</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleFixIssue(issue)}
                        disabled={fixingIssue === issue.id}
                      >
                        {fixingIssue === issue.id ? (
                          <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Wrench className="h-4 w-4 mr-1" />
                        )}
                        {fixingIssue === issue.id ? 'Fixing...' : 'Fix'}
                      </Button>
                      
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleViewIssue(issue)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle className="flex items-center space-x-2">
                              <TypeIcon className={`h-5 w-5 ${iconColor}`} />
                              <span>{issue.title}</span>
                              <Badge variant="outline">{issue.severity}</Badge>
                            </DialogTitle>
                            <DialogDescription>
                              Detailed information about this data quality issue
                            </DialogDescription>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            <div>
                              <h4 className="font-medium text-sm text-gray-700 mb-1">Description</h4>
                              <p className="text-sm text-gray-600">{issue.description}</p>
                            </div>
                            
                            <div>
                              <h4 className="font-medium text-sm text-gray-700 mb-1">Location</h4>
                              <p className="text-sm text-gray-600 font-mono bg-gray-50 p-2 rounded">{issue.path}</p>
                            </div>
                            
                            <div>
                              <h4 className="font-medium text-sm text-gray-700 mb-1">Entity Details</h4>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>
                                  <span className="font-medium">Type:</span> {issue.type}
                                </div>
                                <div>
                                  <span className="font-medium">Severity:</span> {issue.severity}
                                </div>
                                <div>
                                  <span className="font-medium">Entity Type:</span> {issue.entityType}
                                </div>
                                <div>
                                  <span className="font-medium">Entity Key:</span> {issue.entityKey}
                                </div>
                              </div>
                            </div>
                            
                            {issue.suggestions && issue.suggestions.length > 0 && (
                              <div>
                                <h4 className="font-medium text-sm text-gray-700 mb-2">Suggested Fixes</h4>
                                <ul className="space-y-1">
                                  {issue.suggestions.map((suggestion: string, index: number) => (
                                    <li key={index} className="flex items-start space-x-2 text-sm text-gray-600">
                                      <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                      <span>{suggestion}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            <div className="pt-4 border-t">
                              <Button 
                                onClick={() => handleFixIssue(issue)}
                                disabled={fixingIssue === issue.id}
                                className="w-full"
                              >
                                {fixingIssue === issue.id ? (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    Fixing Issue...
                                  </>
                                ) : (
                                  <>
                                    <Wrench className="h-4 w-4 mr-2" />
                                    Apply Automatic Fix
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <p className='text-md'>No data quality issues found. Your data looks good!</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
